import React from 'react';
import { Box, Divider, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useWhiteboardControl } from 'features/toolbar/toolbar/controls/whiteboard/useWhiteboardControl';
import ToolbarMenuItem from 'features/toolbar/ToolbarMenuItem/ToolbarMenuItem';
import {
  selectContentLibraryActiveWhiteboards,
  selectContentLibraryOpenedFile,
} from 'features/content-library/contentLibrarySlice';
import { whiteboardViewed } from 'features/content-library/actions';
import { selectIsLockedContentReadOnly } from 'features/layout/features/content/contentSlice';
import { ContentLockedAction } from 'features/layout/features/content/modals/ContentLockedByModeratorModal';
import { useBulkCloseScopeControl } from 'features/layout/grid-panel/useBulkCloseScopeControl';
import { openModal } from 'features/modal/modalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { dispatch } from 'store/store';
import Icon from 'components/Icon';
import RestoreWhiteboardItem from 'features/whiteboard/drafts/RestoreWhiteboardItem';

const WhiteboardControl = () => {
  const appDispatch = useAppDispatch();
  const openedFile = useAppSelector(selectContentLibraryOpenedFile);

  const whiteboards = useAppSelector(selectContentLibraryActiveWhiteboards);

  const isContentLockedForUser = useAppSelector(selectIsLockedContentReadOnly);

  const {
    active,
    icon,
    privateIcon,
    label,
    privateLabel,
    publicLabel,
    createWhiteboard,
    isFeatureAvailable,
    canManagePrivateWhiteboards,
  } = useWhiteboardControl();

  const {
    enabled: bulkCloseEnabled,
    handleClick: bulkClose,
    label: bulkCloseLabel,
  } = useBulkCloseScopeControl('whiteboards');

  const selectWhiteboard = (index: number) => {
    if (isContentLockedForUser) {
      appDispatch(
        openModal('contentLockedByModerator', { contentAction: ContentLockedAction.WhiteboardOpen })
      );
      return;
    }

    dispatch(whiteboardViewed(whiteboards[index].id));
  };

  const handleCreateWhiteboard = (params?: Parameters<typeof createWhiteboard>[0]) => {
    if (isContentLockedForUser) {
      appDispatch(
        openModal('contentLockedByModerator', {
          contentAction: ContentLockedAction.WhiteboardCreate,
        })
      );
      return;
    }

    createWhiteboard(params);
  };

  if (!isFeatureAvailable) {
    return null;
  }

  return (
    <ToolbarMenuItem
      menu
      key={String(active)}
      id="whiteboard-control"
      overlay={label}
      ariaLabel={label}
      active={active}
      icon="whiteboard"
      MenuProps={{
        PopperProps: { sx: (theme) => ({ zIndex: theme.zIndex.popper }) },
        closeOnItemClick: true,
      }}
    >
      <Box sx={{ maxHeight: 'calc(min(200px, 30vh))', overflow: 'auto', maxWidth: '100%' }}>
        {whiteboards.map((item, index) => (
          <MenuItem
            key={item.id}
            onClick={() => selectWhiteboard(index)}
            sx={
              item.id === openedFile?.id
                ? (theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  })
                : null
            }
          >
            <ListItemText>{item.name}</ListItemText>
          </MenuItem>
        ))}
      </Box>
      {bulkCloseEnabled && (
        <>
          <Divider sx={{ mb: 1 }} />
          <MenuItem onClick={bulkClose}>
            <ListItemText>{bulkCloseLabel}</ListItemText>
          </MenuItem>
        </>
      )}
      <Divider hidden={whiteboards.length === 0} sx={{ mb: 1 }} />
      <MenuItem onClick={() => handleCreateWhiteboard({ private: false })}>
        <ListItemIcon sx={{ ml: -1 }}>
          <Icon name={icon} />
        </ListItemIcon>
        <ListItemText>{canManagePrivateWhiteboards ? publicLabel : label}</ListItemText>
      </MenuItem>
      {canManagePrivateWhiteboards && (
        <MenuItem onClick={() => handleCreateWhiteboard({ private: true })}>
          <ListItemIcon sx={{ ml: -1 }}>
            <Icon name={privateIcon} />
          </ListItemIcon>
          <ListItemText>{privateLabel}</ListItemText>
        </MenuItem>
      )}
      <RestoreWhiteboardItem />
    </ToolbarMenuItem>
  );
};

export default WhiteboardControl;
